import { Injectable, signal } from '@angular/core';
import { nanoid } from 'nanoid';
import { ToastMessage } from '../models/toast';

/** Default visible duration (ms) when `ToastService.show` omits `duration`. */
export const DEFAULT_TOAST_DURATION_MS = 3000;

/**
 * Root-level toast queue. Pair with the `Toast` component from `@aurrica-web/ui`
 * mounted once in the app shell.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly messages = signal<ToastMessage[]>([]);
  private readonly dismissTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  /** Active toasts, newest last. */
  readonly toasts = this.messages.asReadonly();

  /** Enqueues a toast and schedules auto-dismiss after `duration` (ms). */
  show(toast: Partial<ToastMessage> & { message: string }): void {
    const id = nanoid();
    const fullToast: ToastMessage = {
      id,
      message: toast.message,
      detail: toast.detail,
      type: toast.type ?? 'info',
      duration: toast.duration ?? DEFAULT_TOAST_DURATION_MS,
    };
    this.messages.update((prev) => [...prev, fullToast]);

    const handle = setTimeout(() => {
      this.dismissTimers.delete(id);
      this.dismiss(id);
    }, fullToast.duration);
    this.dismissTimers.set(id, handle);
  }

  /** Removes a toast and cancels its pending auto-dismiss timer. */
  dismiss(id: string): void {
    const handle = this.dismissTimers.get(id);
    if (handle !== undefined) {
      clearTimeout(handle);
      this.dismissTimers.delete(id);
    }
    this.messages.update((prev) => prev.filter((t) => t.id !== id));
  }
}
